import React from "react";
import { useCentralStore } from "@/dentlab/src/store/Central";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@/dentlab/src/components/ui/card";
import { Label } from "@/dentlab/src/components/ui/label";
import { Checkbox } from "@/dentlab/src/components/ui/checkbox";
import { Button } from "@/dentlab/src/components/ui/button";
import { ImageInput } from "../../../../forms/form-components/client/input-components/image-input";
import { LanguageSelect } from "../../../../forms/form-input-components/selects/language.select";
import { Region, StorageBucketsEnum } from "@/dentlab/src/types/enums";
import { Textarea } from "@/dentlab/src/components/ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/dentlab/src/components/ui/select";
import { TitleAndNameGroupShadcn } from "@/dentlab/src/forms/form-input-components/groups/title-and-name.group";
import { AddressGroupShadcn } from "@/dentlab/src/forms/form-input-components/groups/address.group";
import { CustomTextFieldShadcn } from "@/dentlab/src/forms/form-input-components/custom-input-components/custom-text-field-shadcn.component";
import { ABRECHNUNGSBEREICH_OPTIONS } from "@/lib/german-xml/types";
import {
	isRegion,
	resolveRegionKind,
} from "@/dentlab/src/lib/utils/region-utils";
import { Input } from "@/dentlab/src/components/ui/input";
import { TooltipWrapper } from "@/dentlab/src/components/ui/tooltip-wrapper";
import { Info } from "lucide-react";

export const AllgemeineInformationenPage: React.FC = () => {
	const {
		client,
		organization,
		clientFormTextFields,
		updateClientFormTextField,
		handleImmediateClientFieldUpdate,
		setPendingAvatarFile,
		uploadClientAvatar,
		pendingAvatarFile,
		activatedRegions,
	} = useCentralStore((state) => ({
		client: state.client,
		organization: state.organization,
		clientFormTextFields: state.clientFormTextFields,
		updateClientFormTextField: state.updateClientFormTextField,
		handleImmediateClientFieldUpdate:
			state.handleImmediateClientFieldUpdate,
		setPendingAvatarFile: state.setPendingAvatarFile,
		uploadClientAvatar: state.uploadClientAvatar,
		pendingAvatarFile: state.pendingAvatarFile,
		activatedRegions: state.activatedRegions,
	}));

	const regionId =
		client?.default_activated_region_id ??
		organization?.default_activated_region_id;

	// EORI is only relevant for cross-border work: show it when the client's region
	// differs from the lab's own region (analogous to the Abrechnungsbereich field).
	const orgRegion = resolveRegionKind(null, organization, activatedRegions);
	const clientRegion = resolveRegionKind(
		client,
		organization,
		activatedRegions
	);
	const showEoriField =
		orgRegion != null && clientRegion != null && orgRegion !== clientRegion;

	if (!client) {
		return (
			<div className="p-6">
				<div className="text-gray-500">Kein Kunde ausgewählt</div>
			</div>
		);
	}

	const handleFileChange = (file: File | null) => {
		setPendingAvatarFile(file);
	};

	return (
		<div className="p-6">
			<div className="max-w-6xl">
				<h1 className="text-2xl font-semibold text-gray-900 mb-6">
					Allgemeine Informationen
				</h1>
				<div className="space-y-6">
					{/* Stammdaten Section */}
					<Card>
						<CardHeader>
							<CardTitle>Stammdaten</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="flex items-start gap-8">
								<div className="flex-shrink-0">
									<ImageInput
										organizationId={organization?.id ?? ""}
										bucket={StorageBucketsEnum.ORG_V1}
										avatarPath={client.avatar_path ?? ""}
										onFileChange={handleFileChange}
									/>
									{pendingAvatarFile && (
										<Button
											onClick={uploadClientAvatar}
											size="sm"
											className="mt-2 w-full"
										>
											Avatar speichern
										</Button>
									)}
								</div>
								<div className="flex-1">
									<CustomTextFieldShadcn
										id="code"
										name="code"
										label="Kürzel"
										value={clientFormTextFields.code || ""}
										onChange={(e) =>
											updateClientFormTextField(
												"code",
												e.target.value
											)
										}
										classNameInput="max-w-xs"
										required
									/>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* Name und Adresse Section */}
					<Card>
						<CardHeader>
							<CardTitle>Name und Adresse</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="flex items-center space-x-2">
								<Checkbox
									id="is_company"
									checked={client.is_company ? true : false}
									onCheckedChange={(checked) =>
										handleImmediateClientFieldUpdate(
											"is_company",
											checked as boolean
										)
									}
								/>
								<Label htmlFor="is_company">
									Firmenadresse
								</Label>
							</div>

							{!client.is_company && (
								<TitleAndNameGroupShadcn
									formData={clientFormTextFields}
									formErrors={{}}
									onChange={(e) =>
										updateClientFormTextField(
											e.target.name,
											e.target.value
										)
									}
									onValueChange={({ name, value }) =>
										updateClientFormTextField(name, value)
									}
									requiredFields={["last_name"]}
								/>
							)}

							<CustomTextFieldShadcn
								id="company_name"
								name="company_name"
								label={
									client.is_company
										? "Firma / Organisation"
										: "Firma / Organisation (optional)"
								}
								value={clientFormTextFields.company_name || ""}
								onChange={(e) =>
									updateClientFormTextField(
										"company_name",
										e.target.value
									)
								}
								required={client.is_company ?? false}
							/>

							<AddressGroupShadcn
								formData={clientFormTextFields}
								formErrors={{}}
								onChange={(e) =>
									updateClientFormTextField(
										e.target.name,
										e.target.value
									)
								}
								onValueChange={({ name, value }) =>
									updateClientFormTextField(name, value)
								}
								requiredFields={[
									"street",
									"city",
									"postal_code",
									"country",
								]}
							/>

							<div className="flex items-center space-x-2 mt-4">
								<Checkbox
									id="is_correspondence_address"
									checked={
										client.is_correspondence_address
											? true
											: false
									}
									onCheckedChange={(checked) =>
										handleImmediateClientFieldUpdate(
											"is_correspondence_address",
											checked as boolean
										)
									}
								/>
								<Label htmlFor="is_correspondence_address">
									Korrespondenzadresse
								</Label>
							</div>

							{client.is_correspondence_address && (
								<div className="mt-4 p-4 border rounded-lg space-y-4">
									<div className="flex items-center space-x-2">
										<Checkbox
											id="c_is_company"
											checked={
												client.c_is_company
													? true
													: false
											}
											onCheckedChange={(checked) =>
												handleImmediateClientFieldUpdate(
													"c_is_company",
													checked as boolean
												)
											}
										/>
										<Label htmlFor="c_is_company">
											Korrespondenzadresse ist eine
											Firmenadresse
										</Label>
									</div>

									{!client.c_is_company && (
										<TitleAndNameGroupShadcn
											formData={clientFormTextFields}
											formErrors={{}}
											onChange={(e) =>
												updateClientFormTextField(
													e.target.name,
													e.target.value
												)
											}
											onValueChange={({ name, value }) =>
												updateClientFormTextField(
													name,
													value
												)
											}
											customNaming={{
												title: "c_title",
												first_name: "c_first_name",
												last_name: "c_last_name",
											}}
											requiredFields={["last_name"]}
										/>
									)}

									<CustomTextFieldShadcn
										id="c_company_name"
										name="c_company_name"
										label={
											client.c_is_company
												? "Firma / Organisation (Korrespondenz)"
												: "Firma / Organisation (Korrespondenz) (optional)"
										}
										value={
											clientFormTextFields.c_company_name ||
											""
										}
										onChange={(e) =>
											updateClientFormTextField(
												"c_company_name",
												e.target.value
											)
										}
										required={client.c_is_company ?? false}
									/>

									<AddressGroupShadcn
										formData={clientFormTextFields}
										formErrors={{}}
										onChange={(e) =>
											updateClientFormTextField(
												e.target.name,
												e.target.value
											)
										}
										onValueChange={({ name, value }) =>
											updateClientFormTextField(
												name,
												value
											)
										}
										customNaming={{
											street: "c_street",
											country: "c_country",
											city: "c_city",
											postal_code: "c_postal_code",
											additional_information:
												"c_additional_information",
											phone_personal: "c_phone_personal",
											phone_business: "c_phone_business",
											phone_mobile: "c_phone_mobile",
											fax: "c_fax",
											email: "c_email",
										}}
										requiredFields={[
											"street",
											"city",
											"postal_code",
											"country",
										]}
									/>
								</div>
							)}
						</CardContent>
					</Card>

					{/* Sonstiges Section */}
					<Card>
						<CardHeader>
							<CardTitle>Sonstiges</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div>
									<Label htmlFor="language">Sprache</Label>
									<LanguageSelect
										value={client.language || "de"}
										onChange={(value) =>
											handleImmediateClientFieldUpdate(
												"language",
												value
											)
										}
									/>
								</div>
								<CustomTextFieldShadcn
									id="external_reference"
									name="external_reference"
									label="Externe Referenz"
									value={
										clientFormTextFields.external_reference ||
										""
									}
									onChange={(e) =>
										updateClientFormTextField(
											"external_reference",
											e.target.value
										)
									}
								/>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{showEoriField && (
									<div>
										<div className="flex items-center gap-1">
											<Label htmlFor="eori_number">
												EORI-Nummer
											</Label>
											<TooltipWrapper title="EORI-Nummer: Zoll-Identifikationsnummer für den grenzüberschreitenden Warenverkehr in der EU. Optional und wird als Platzhalter (#EORI) in Dokumentkommentaren verwendet.">
												<button
													type="button"
													aria-label="Info zur EORI-Nummer"
													className="text-gray-400 hover:text-gray-600"
												>
													<Info className="h-4 w-4" />
												</button>
											</TooltipWrapper>
										</div>
										<Input
											id="eori_number"
											name="eori_number"
											aria-label="EORI-Nummer"
											data-testid="custom-text-field-eori_number"
											value={
												clientFormTextFields.eori_number ||
												""
											}
											onChange={(e) =>
												updateClientFormTextField(
													"eori_number",
													e.target.value
												)
											}
										/>
									</div>
								)}
								{regionId &&
									isRegion(
										regionId,
										Region.DE,
										activatedRegions
									) && (
										<div>
											<Label htmlFor="billing_region">
												Abrechnungsbereich
											</Label>
											<Select
												value={
													client.billing_region ||
													"NONE"
												}
												onValueChange={(value) =>
													handleImmediateClientFieldUpdate(
														"billing_region",
														value !== "NONE"
															? value
															: null
													)
												}
											>
												<SelectTrigger
													id="billing_region"
													className="mt-1"
												>
													<SelectValue placeholder="Abrechnungsbereich wählen..." />
												</SelectTrigger>
												<SelectContent>
													{ABRECHNUNGSBEREICH_OPTIONS.map(
														(option) => (
															<SelectItem
																key={
																	option.value
																}
																value={
																	option.value
																}
															>
																{option.value !==
																	"NONE" &&
																	option.value +
																		" - "}{" "}
																{option.label}
															</SelectItem>
														)
													)}
												</SelectContent>
											</Select>
										</div>
									)}
							</div>

							<div>
								<Label htmlFor="note">Kurznotiz</Label>
								<Textarea
									id="note"
									name="note"
									value={clientFormTextFields.note || ""}
									onChange={(e) =>
										updateClientFormTextField(
											"note",
											e.target.value
										)
									}
									rows={3}
									className="resize-none"
								/>
							</div>

							<div className="space-y-4">
								<div className="flex items-center space-x-2">
									<Checkbox
										id="is_inactive"
										checked={
											client.is_inactive ? true : false
										}
										onCheckedChange={(checked) =>
											handleImmediateClientFieldUpdate(
												"is_inactive",
												checked as boolean
											)
										}
									/>
									<Label htmlFor="is_inactive">Inaktiv</Label>
								</div>

								{client.is_inactive && (
									<div>
										<Label htmlFor="inactive_reason">
											Grund der Inaktivität
										</Label>
										<Select
											value={client.inactive_reason || ""}
											onValueChange={(value) =>
												handleImmediateClientFieldUpdate(
													"inactive_reason",
													value || null
												)
											}
										>
											<SelectTrigger>
												<SelectValue placeholder="Grund auswählen..." />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="NO_LONGER_CUSTOMER">
													Kein Kunde mehr
												</SelectItem>
												<SelectItem value="TEMPORARY_INACTIVE">
													Vorübergehend inaktiv
												</SelectItem>
												<SelectItem value="DECEASED">
													Verstorben
												</SelectItem>
												<SelectItem value="OTHER">
													Sonstiges
												</SelectItem>
											</SelectContent>
										</Select>
									</div>
								)}
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	);
};
